import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, Cpu, Search, CheckCircle, Database, Layers, 
  Target, BookOpen, Zap, Filter, Tag, ExternalLink, X,
  Users, Building2, Landmark, MessageSquare, Palette, Code, Kanban,
  ChevronDown, Check, Shield, Gauge, Settings, GraduationCap,
  Clock, DollarSign, Boxes, ChevronUp, Sparkles, TrendingUp,
  CircleDot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import BackNavigation from "@/components/BackNavigation";
import { 
  AI_REGISTRY, 
  AI_CATEGORIES, 
  AI_FUNCTIONS,
  EVALUATION_STATUS,
  INTEGRATION_COMPLEXITY,
  AI_READINESS_LEVEL,
  TOOL_STACKS,
  getCategoryStats,
  getLabStats,
  getToolById,
  getToolStacksByLab,
  getStackTools
} from "@/data/aiRegistry";

// Icon mapping for categories
const CATEGORY_ICONS = {
  "content-writing": BookOpen,
  "data-analytics": Database,
  "automation-workflow": Zap,
  "communication": MessageSquare,
  "research-learning": Search,
  "creative-design": Palette,
  "coding-development": Code,
  "project-management": Kanban
};

// Lab icons
const LAB_ICONS = {
  workforce: Users,
  organization: Building2,
  institutional: Landmark
};

// Integration Complexity Display
const COMPLEXITY_DISPLAY = {
  [INTEGRATION_COMPLEXITY.LOW]: { label: "Easy Setup", color: "text-green-600", bg: "bg-green-100" },
  [INTEGRATION_COMPLEXITY.MEDIUM]: { label: "Moderate", color: "text-yellow-600", bg: "bg-yellow-100" },
  [INTEGRATION_COMPLEXITY.HIGH]: { label: "Advanced", color: "text-red-600", bg: "bg-red-100" }
};

// AI Readiness Level Display
const READINESS_DISPLAY = {
  [AI_READINESS_LEVEL.BASIC]: { label: "Beginner Friendly", color: "text-green-600", icon: GraduationCap },
  [AI_READINESS_LEVEL.INTERMEDIATE]: { label: "Some Experience", color: "text-yellow-600", icon: GraduationCap },
  [AI_READINESS_LEVEL.ADVANCED]: { label: "Expert Level", color: "text-red-600", icon: GraduationCap }
};

// Score color helper
const getScoreColor = (score) => {
  if (score >= 90) return "text-green-600";
  if (score >= 75) return "text-yellow-600";
  return "text-red-600";
};

const getScoreBg = (score) => {
  if (score >= 90) return "bg-green-500";
  if (score >= 75) return "bg-yellow-500";
  return "bg-red-500";
};

/**
 * AI Capability Registry Page
 * Central browsing interface for all AI tools in the Zodiac ecosystem
 */
export default function AICapabilityRegistry() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [expandedTool, setExpandedTool] = useState(null);
  const [activeTab, setActiveTab] = useState("tools"); // "tools" or "stacks"
  const [selectedStackLab, setSelectedStackLab] = useState("workforce");
  
  // Get filter values from URL params
  const selectedCategory = searchParams.get("category") || "";
  const selectedLab = searchParams.get("lab") || "";
  const selectedFunction = searchParams.get("function") || "";
  const verifiedOnly = searchParams.get("verified") === "true";
  
  // Update URL params
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };
  
  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    setSearchQuery("");
  };
  
  // Filter tools
  const filteredTools = useMemo(() => {
    return AI_REGISTRY.filter(tool => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          tool.name.toLowerCase().includes(query) ||
          tool.provider.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Category filter
      if (selectedCategory && tool.category !== selectedCategory) return false;
      
      // Lab filter
      if (selectedLab && !tool.labRelevance[selectedLab]?.relevant) return false;
      
      // Function filter
      if (selectedFunction && !tool.functions.includes(selectedFunction)) return false;
      
      // Verified filter
      if (verifiedOnly && tool.evaluation.status !== EVALUATION_STATUS.VERIFIED) return false;
      
      return true;
    });
  }, [searchQuery, selectedCategory, selectedLab, selectedFunction, verifiedOnly]);
  
  const categoryStats = getCategoryStats();
  const labStats = getLabStats();
  const hasActiveFilters = selectedCategory || selectedLab || selectedFunction || verifiedOnly || searchQuery;
  
  // Get tool stacks filtered by selected lab
  const filteredStacks = getToolStacksByLab(selectedStackLab);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="ai-registry-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation fallbackPath="/observatories/ai" fallbackLabel="AI Observatory" />
      </div>

      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/observatories" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
              Observatories
            </Link>
            <span className="text-[#1E3A8A]/40">/</span>
            <Link to="/observatories/ai" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
              AI Observatory
            </Link>
            <span className="text-[#1E3A8A]/40">/</span>
            <span className="text-sm text-[#1E3A8A] font-medium">Registry</span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#991B1B' }}>
                Zodiac AI Capability Registry
              </h1>
              <p className="text-[#1E3A8A]/70">
                {AI_REGISTRY.length} AI tools evaluated for workforce intelligence development
              </p>
            </div>
            
            {/* Search - Only show for tools tab */}
            {activeTab === "tools" && (
              <div className="relative w-full lg:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1E3A8A]/40" />
                <Input
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 border-[#E5E7EB] focus:border-[#1E3A8A]"
                  data-testid="search-input"
                />
              </div>
            )}
          </div>
          
          {/* Tabs: Tools & Tool Stacks */}
          <div className="flex gap-1 mt-8 border-b border-[#E5E7EB]">
            <button
              onClick={() => setActiveTab("tools")}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "tools"
                  ? 'border-[#991B1B] text-[#991B1B]'
                  : 'border-transparent text-[#1E3A8A]/60 hover:text-[#1E3A8A]'
              }`}
              data-testid="tab-tools"
            >
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4" />
                AI Tools ({AI_REGISTRY.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab("stacks")}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "stacks"
                  ? 'border-[#991B1B] text-[#991B1B]'
                  : 'border-transparent text-[#1E3A8A]/60 hover:text-[#1E3A8A]'
              }`}
              data-testid="tab-stacks"
            >
              <div className="flex items-center gap-2">
                <Boxes className="w-4 h-4" />
                Tool Stacks ({TOOL_STACKS.length})
              </div>
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* TOOLS TAB CONTENT */}
        {activeTab === "tools" && (
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className={`lg:w-72 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 sticky top-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-[#991B1B]">Filters</h3>
                  {hasActiveFilters && (
                    <button 
                      onClick={clearFilters}
                      className="text-xs text-[#1E3A8A] hover:underline"
                      data-testid="clear-filters-btn"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                
                {/* Lab Relevance Filter */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#1E3A8A] mb-3">Lab Relevance</p>
                  <div className="space-y-2">
                    {[
                      { id: "workforce", name: "Workforce Lab", count: labStats.workforce },
                      { id: "organization", name: "Organization Lab", count: labStats.organization },
                      { id: "institutional", name: "Institutional Lab", count: labStats.institutional }
                    ].map(lab => {
                      const Icon = LAB_ICONS[lab.id];
                      const isSelected = selectedLab === lab.id;
                      return (
                        <button
                          key={lab.id}
                          onClick={() => updateFilter("lab", isSelected ? "" : lab.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                            isSelected 
                              ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]' 
                              : 'hover:bg-[#F8FAFC] text-[#1E3A8A]/70'
                          }`}
                          data-testid={`filter-lab-${lab.id}`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm flex-1">{lab.name}</span>
                          <span className="text-xs text-[#1E3A8A]/50">{lab.count}</span>
                          {isSelected && <Check className="w-4 h-4" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Category Filter */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#1E3A8A] mb-3">Category</p>
                  <div className="space-y-2">
                    {categoryStats.map(cat => {
                      const Icon = CATEGORY_ICONS[cat.id] || Cpu;
                      const isSelected = selectedCategory === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => updateFilter("category", isSelected ? "" : cat.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                            isSelected 
                              ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]' 
                              : 'hover:bg-[#F8FAFC] text-[#1E3A8A]/70'
                          }`}
                          data-testid={`filter-category-${cat.id}`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm flex-1">{cat.name}</span>
                          <span className="text-xs text-[#1E3A8A]/50">{cat.count}</span>
                          {isSelected && <Check className="w-4 h-4" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Verified Only Toggle */}
                <div className="mb-6">
                  <button
                    onClick={() => updateFilter("verified", verifiedOnly ? "" : "true")}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      verifiedOnly 
                        ? 'bg-green-50 border-green-200 text-green-700' 
                        : 'border-[#E5E7EB] hover:bg-[#F8FAFC] text-[#1E3A8A]/70'
                    }`}
                    data-testid="filter-verified"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Zodiac Verified Only</span>
                    {verifiedOnly && <Check className="w-4 h-4 ml-auto" />}
                  </button>
                </div>
                
                {/* Function Filter */}
                <div>
                  <p className="text-sm font-medium text-[#1E3A8A] mb-3">Function</p>
                  <div className="flex flex-wrap gap-2">
                    {AI_FUNCTIONS.slice(0, 8).map(func => {
                      const isSelected = selectedFunction === func;
                      return (
                        <button
                          key={func}
                          onClick={() => updateFilter("function", isSelected ? "" : func)}
                          className={`px-3 py-1 rounded-full text-xs transition-colors ${
                            isSelected 
                              ? 'bg-[#1E3A8A] text-white' 
                              : 'bg-[#F8FAFC] text-[#1E3A8A]/70 hover:bg-[#1E3A8A]/10'
                          }`}
                        >
                          {func}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </aside>

            {/* Results */}
            <main className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#1E3A8A]/70">
                  Showing <span className="font-medium text-[#1E3A8A]">{filteredTools.length}</span> tools
                  {hasActiveFilters && " (filtered)"}
                </p>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 text-sm text-[#1E3A8A]"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
              </div>
              
              {/* Active Filters */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedLab && (
                    <Badge variant="secondary" className="bg-[#1E3A8A]/10 text-[#1E3A8A] hover:bg-[#1E3A8A]/20">
                      {selectedLab.charAt(0).toUpperCase() + selectedLab.slice(1)} Lab
                      <button onClick={() => updateFilter("lab", "")} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {selectedCategory && (
                    <Badge variant="secondary" className="bg-[#1E3A8A]/10 text-[#1E3A8A] hover:bg-[#1E3A8A]/20">
                      {AI_CATEGORIES.find(c => c.id === selectedCategory)?.name}
                      <button onClick={() => updateFilter("category", "")} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {selectedFunction && (
                    <Badge variant="secondary" className="bg-[#1E3A8A]/10 text-[#1E3A8A] hover:bg-[#1E3A8A]/20">
                      {selectedFunction}
                      <button onClick={() => updateFilter("function", "")} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {verifiedOnly && (
                    <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200">
                      Verified Only
                      <button onClick={() => updateFilter("verified", "")} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                </div>
              )}

              {/* Tools Grid */}
              {filteredTools.length > 0 ? (
                <div className="space-y-4">
                  {filteredTools.map((tool, index) => {
                    const CategoryIcon = CATEGORY_ICONS[tool.category] || Cpu;
                    const isExpanded = expandedTool === tool.id;
                    const complexityInfo = COMPLEXITY_DISPLAY[tool.scores?.integrationComplexity] || COMPLEXITY_DISPLAY[INTEGRATION_COMPLEXITY.MEDIUM];
                    const readinessInfo = READINESS_DISPLAY[tool.scores?.aiReadinessLevel] || READINESS_DISPLAY[AI_READINESS_LEVEL.INTERMEDIATE];
                    
                    return (
                      <motion.div
                        key={tool.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}
                        data-testid={`tool-card-${tool.id}`}
                      >
                        <Card className={`border-[#E5E7EB] transition-all ${isExpanded ? 'shadow-lg border-[#1E3A8A]/30' : 'hover:shadow-md hover:border-[#1E3A8A]/20'}`}>
                          <CardContent className="p-0">
                            {/* Main Card Content */}
                            <div className="p-6">
                              <div className="flex items-start gap-4">
                                {/* Tool Icon */}
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E3A8A]/10 to-[#991B1B]/10 flex items-center justify-center flex-shrink-0">
                                  <CategoryIcon className="w-7 h-7 text-[#1E3A8A]" />
                                </div>
                                
                                {/* Tool Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-3 mb-2">
                                    <div>
                                      <h3 className="font-heading text-lg font-bold text-[#991B1B]">
                                        {tool.name}
                                      </h3>
                                      <p className="text-sm text-[#1E3A8A]/60">{tool.provider}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {tool.evaluation.status === EVALUATION_STATUS.VERIFIED && (
                                        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                                          <CheckCircle className="w-3.5 h-3.5" />
                                          <span className="text-xs font-medium">Verified</span>
                                        </div>
                                      )}
                                      {tool.evaluation.status === EVALUATION_STATUS.UNDER_REVIEW && (
                                        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700">
                                          <CircleDot className="w-3.5 h-3.5" />
                                          <span className="text-xs font-medium">Under Review</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <p className="text-sm text-[#1E3A8A]/70 mb-4 line-clamp-2">
                                    {tool.description}
                                  </p>
                                  
                                  {/* Scores Row */}
                                  {tool.scores && (
                                    <div className="flex flex-wrap gap-4 mb-4">
                                      {/* Reliability Score */}
                                      <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-[#1E3A8A]/50" />
                                        <span className="text-xs text-[#1E3A8A]/60">Reliability</span>
                                        <span className={`text-sm font-bold ${getScoreColor(tool.scores.reliability)}`}>
                                          {tool.scores.reliability}%
                                        </span>
                                      </div>
                                      
                                      {/* Workforce Fit Score */}
                                      <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-[#1E3A8A]/50" />
                                        <span className="text-xs text-[#1E3A8A]/60">Workforce Fit</span>
                                        <span className={`text-sm font-bold ${getScoreColor(tool.scores.workforceFit)}`}>
                                          {tool.scores.workforceFit}%
                                        </span>
                                      </div>
                                      
                                      {/* Integration Complexity */}
                                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${complexityInfo.bg}`}>
                                        <Settings className={`w-3 h-3 ${complexityInfo.color}`} />
                                        <span className={`text-xs font-medium ${complexityInfo.color}`}>
                                          {complexityInfo.label}
                                        </span>
                                      </div>
                                      
                                      {/* AI Readiness */}
                                      <div className="flex items-center gap-1 text-[#1E3A8A]/60">
                                        <GraduationCap className="w-3.5 h-3.5" />
                                        <span className={`text-xs ${readinessInfo.color}`}>
                                          {readinessInfo.label}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Lab Relevance & Functions */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    {tool.labRelevance.workforce?.relevant && (
                                      <div className="flex items-center gap-1 px-2 py-1 rounded bg-[#1E3A8A]/5 text-[#1E3A8A]">
                                        <Users className="w-3 h-3" />
                                        <span className="text-xs">Workforce</span>
                                        <span className="text-xs font-bold ml-1">{tool.labRelevance.workforce.fitScore}%</span>
                                      </div>
                                    )}
                                    {tool.labRelevance.organization?.relevant && (
                                      <div className="flex items-center gap-1 px-2 py-1 rounded bg-[#2563EB]/5 text-[#2563EB]">
                                        <Building2 className="w-3 h-3" />
                                        <span className="text-xs">Organization</span>
                                        <span className="text-xs font-bold ml-1">{tool.labRelevance.organization.fitScore}%</span>
                                      </div>
                                    )}
                                    {tool.labRelevance.institutional?.relevant && (
                                      <div className="flex items-center gap-1 px-2 py-1 rounded bg-[#3B82F6]/5 text-[#3B82F6]">
                                        <Landmark className="w-3 h-3" />
                                        <span className="text-xs">Institutional</span>
                                        <span className="text-xs font-bold ml-1">{tool.labRelevance.institutional.fitScore}%</span>
                                      </div>
                                    )}
                                    
                                    <div className="h-4 w-px bg-[#E5E7EB] mx-1" />
                                    
                                    {tool.functions.slice(0, 2).map(func => (
                                      <span 
                                        key={func} 
                                        className="text-xs px-2 py-1 rounded-full bg-[#F8FAFC] text-[#1E3A8A]/60"
                                      >
                                        {func}
                                      </span>
                                    ))}
                                    {tool.functions.length > 2 && (
                                      <span className="text-xs text-[#1E3A8A]/50">
                                        +{tool.functions.length - 2}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Footer */}
                              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7EB]">
                                <div className="flex items-center gap-4">
                                  <span className="text-xs text-[#1E3A8A]/50">{tool.pricing}</span>
                                  <a 
                                    href={tool.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline"
                                  >
                                    Visit
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                </div>
                                <button
                                  onClick={() => setExpandedTool(isExpanded ? null : tool.id)}
                                  className="flex items-center gap-1 text-sm text-[#991B1B] hover:text-[#991B1B]/80"
                                  data-testid={`expand-tool-${tool.id}`}
                                >
                                  {isExpanded ? "Hide Details" : "View Use Cases"}
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            
                            {/* Expanded Detail Section */}
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-6 pb-6 pt-2 bg-[#F8FAFC] border-t border-[#E5E7EB]">
                                    {/* Use Cases by Lab */}
                                    <div className="grid md:grid-cols-3 gap-4">
                                      {/* Workforce Use Cases */}
                                      {tool.labRelevance.workforce?.relevant && tool.labRelevance.workforce.useCases?.length > 0 && (
                                        <div className="bg-white rounded-lg p-4 border border-[#E5E7EB]">
                                          <div className="flex items-center gap-2 mb-3">
                                            <Users className="w-4 h-4 text-[#1E3A8A]" />
                                            <h4 className="text-sm font-bold text-[#1E3A8A]">Workforce Use Cases</h4>
                                          </div>
                                          <div className="space-y-3">
                                            {tool.labRelevance.workforce.useCases.slice(0, 3).map((uc, idx) => (
                                              <div key={idx}>
                                                <p className="text-sm font-medium text-[#991B1B]">{uc.title}</p>
                                                <p className="text-xs text-[#1E3A8A]/60 mt-0.5">{uc.description}</p>
                                                {uc.outcomes && (
                                                  <div className="flex flex-wrap gap-1 mt-1">
                                                    {uc.outcomes.slice(0, 2).map((outcome, oidx) => (
                                                      <span key={oidx} className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">
                                                        {outcome}
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Organization Use Cases */}
                                      {tool.labRelevance.organization?.relevant && tool.labRelevance.organization.useCases?.length > 0 && (
                                        <div className="bg-white rounded-lg p-4 border border-[#E5E7EB]">
                                          <div className="flex items-center gap-2 mb-3">
                                            <Building2 className="w-4 h-4 text-[#2563EB]" />
                                            <h4 className="text-sm font-bold text-[#2563EB]">Organization Use Cases</h4>
                                          </div>
                                          <div className="space-y-3">
                                            {tool.labRelevance.organization.useCases.slice(0, 3).map((uc, idx) => (
                                              <div key={idx}>
                                                <p className="text-sm font-medium text-[#991B1B]">{uc.title}</p>
                                                <p className="text-xs text-[#1E3A8A]/60 mt-0.5">{uc.description}</p>
                                                {uc.outcomes && (
                                                  <div className="flex flex-wrap gap-1 mt-1">
                                                    {uc.outcomes.slice(0, 2).map((outcome, oidx) => (
                                                      <span key={oidx} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                                        {outcome}
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Institutional Use Cases */}
                                      {tool.labRelevance.institutional?.relevant && tool.labRelevance.institutional.useCases?.length > 0 && (
                                        <div className="bg-white rounded-lg p-4 border border-[#E5E7EB]">
                                          <div className="flex items-center gap-2 mb-3">
                                            <Landmark className="w-4 h-4 text-[#3B82F6]" />
                                            <h4 className="text-sm font-bold text-[#3B82F6]">Institutional Use Cases</h4>
                                          </div>
                                          <div className="space-y-3">
                                            {tool.labRelevance.institutional.useCases.slice(0, 3).map((uc, idx) => (
                                              <div key={idx}>
                                                <p className="text-sm font-medium text-[#991B1B]">{uc.title}</p>
                                                <p className="text-xs text-[#1E3A8A]/60 mt-0.5">{uc.description}</p>
                                                {uc.outcomes && (
                                                  <div className="flex flex-wrap gap-1 mt-1">
                                                    {uc.outcomes.slice(0, 2).map((outcome, oidx) => (
                                                      <span key={oidx} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                                                        {outcome}
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Opportunity Alignment (if available) */}
                                    {tool.opportunityAlignment && (
                                      <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                                        <div className="flex items-center gap-2 mb-3">
                                          <TrendingUp className="w-4 h-4 text-[#991B1B]" />
                                          <h4 className="text-sm font-bold text-[#991B1B]">Opportunity Alignment</h4>
                                        </div>
                                        <div className="grid md:grid-cols-3 gap-4">
                                          {tool.opportunityAlignment.businessOpportunities?.length > 0 && (
                                            <div>
                                              <p className="text-xs font-medium text-[#1E3A8A] mb-1">Business Opportunities</p>
                                              <div className="flex flex-wrap gap-1">
                                                {tool.opportunityAlignment.businessOpportunities.map((opp, idx) => (
                                                  <span key={idx} className="text-xs px-2 py-0.5 rounded bg-[#1E3A8A]/5 text-[#1E3A8A]">
                                                    {opp}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {tool.opportunityAlignment.servicePathways?.length > 0 && (
                                            <div>
                                              <p className="text-xs font-medium text-[#1E3A8A] mb-1">Service Pathways</p>
                                              <div className="flex flex-wrap gap-1">
                                                {tool.opportunityAlignment.servicePathways.map((path, idx) => (
                                                  <span key={idx} className="text-xs px-2 py-0.5 rounded bg-[#2563EB]/5 text-[#2563EB]">
                                                    {path}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {tool.opportunityAlignment.careerPathways?.length > 0 && (
                                            <div>
                                              <p className="text-xs font-medium text-[#1E3A8A] mb-1">Career Pathways</p>
                                              <div className="flex flex-wrap gap-1">
                                                {tool.opportunityAlignment.careerPathways.map((path, idx) => (
                                                  <span key={idx} className="text-xs px-2 py-0.5 rounded bg-[#991B1B]/5 text-[#991B1B]">
                                                    {path}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-[#E5E7EB]">
                  <div className="w-16 h-16 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-[#1E3A8A]/40" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#991B1B] mb-2">
                    No tools found
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/60 mb-4">
                    Try adjusting your filters or search query
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              )}
            </main>
          </div>
        )}
        
        {/* TOOL STACKS TAB CONTENT */}
        {activeTab === "stacks" && (
          <div>
            {/* Stacks Header */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#991B1B] mb-1">
                    Curated Tool Stacks
                  </h2>
                  <p className="text-sm text-[#1E3A8A]/70">
                    Pre-configured combinations of AI tools designed for specific purposes and outcomes
                  </p>
                </div>
                
                {/* Lab Filter for Stacks */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1E3A8A]/60">Filter by Lab:</span>
                  <div className="flex gap-1">
                    {[
                      { id: "workforce", label: "Workforce", icon: Users },
                      { id: "organization", label: "Organization", icon: Building2 },
                      { id: "institutional", label: "Institutional", icon: Landmark }
                    ].map(lab => {
                      const Icon = lab.icon;
                      const isActive = selectedStackLab === lab.id;
                      return (
                        <button
                          key={lab.id}
                          onClick={() => setSelectedStackLab(lab.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isActive 
                              ? 'bg-[#1E3A8A] text-white' 
                              : 'bg-[#F8FAFC] text-[#1E3A8A]/70 hover:bg-[#1E3A8A]/10'
                          }`}
                          data-testid={`stack-filter-${lab.id}`}
                        >
                          <Icon className="w-4 h-4" />
                          {lab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Stacks Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStacks.map((stack, index) => {
                const stackTools = getStackTools(stack.id);
                const readinessInfo = READINESS_DISPLAY[stack.aiReadinessRequired] || READINESS_DISPLAY[AI_READINESS_LEVEL.BASIC];
                const LabIcon = LAB_ICONS[stack.targetLab];
                
                return (
                  <motion.div
                    key={stack.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    data-testid={`stack-card-${stack.id}`}
                  >
                    <Card className="h-full border-[#E5E7EB] hover:shadow-lg hover:border-[#1E3A8A]/20 transition-all">
                      <CardContent className="p-6 flex flex-col h-full">
                        {/* Stack Header */}
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#991B1B]/10 to-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                            <Boxes className="w-6 h-6 text-[#991B1B]" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-[#991B1B]">{stack.name}</h3>
                            <div className="flex items-center gap-1 mt-1">
                              <LabIcon className="w-3 h-3 text-[#1E3A8A]/50" />
                              <span className="text-xs text-[#1E3A8A]/50 capitalize">{stack.targetLab} Lab</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Purpose */}
                        <p className="text-sm text-[#1E3A8A]/70 mb-4">{stack.purpose}</p>
                        
                        {/* Tools in Stack */}
                        <div className="mb-4">
                          <p className="text-xs font-medium text-[#1E3A8A] mb-2">Included Tools ({stackTools.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {stackTools.map(tool => {
                              const ToolIcon = CATEGORY_ICONS[tool?.category] || Cpu;
                              return tool ? (
                                <div 
                                  key={tool.id}
                                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#F8FAFC] border border-[#E5E7EB]"
                                  title={tool.name}
                                >
                                  <ToolIcon className="w-3 h-3 text-[#1E3A8A]" />
                                  <span className="text-xs text-[#1E3A8A]">{tool.name}</span>
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                        
                        {/* Outcomes */}
                        {stack.outcomes && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-[#1E3A8A] mb-2">Expected Outcomes</p>
                            <ul className="space-y-1">
                              {stack.outcomes.slice(0, 3).map((outcome, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs text-[#1E3A8A]/70">
                                  <Sparkles className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  {outcome}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Meta Info */}
                        <div className="mt-auto pt-4 border-t border-[#E5E7EB] space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-1 text-[#1E3A8A]/60">
                              <Clock className="w-3 h-3" />
                              Setup: {stack.estimatedSetupTime}
                            </div>
                            <div className="flex items-center gap-1 text-[#1E3A8A]/60">
                              <DollarSign className="w-3 h-3" />
                              {stack.monthlyInvestment}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <GraduationCap className={`w-3 h-3 ${readinessInfo.color}`} />
                            <span className={`text-xs ${readinessInfo.color}`}>
                              {readinessInfo.label} Required
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            
            {filteredStacks.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-[#E5E7EB]">
                <div className="w-16 h-16 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
                  <Boxes className="w-8 h-8 text-[#1E3A8A]/40" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#991B1B] mb-2">
                  No stacks found for this lab
                </h3>
                <p className="text-sm text-[#1E3A8A]/60">
                  Try selecting a different lab filter
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
